<script lang="ts">
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
	import {
		is_gen_2,
		isGen2EM,
		get_gen_2_component_from_category,
		get_gen_2_component_channel_structure,
	} from 'src/helpers/interface_helpers';
	import { convert } from 'src/helpers/utilities';
	import {
		emeterMainMetric,
		setEmeterMainMetric,
		type EmeterMainMetric,
	} from 'src/helpers/emeter_main_metric';
	import {
		getEmeterHeroReading,
		hasEmeterCurrentReading,
	} from 'src/helpers/emeter_sidepanel_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import { sidepanelChartMode } from 'src/stores/ui/sidepanel-public';
	import { Button, ButtonGroupV2, HeroValue, Text } from '@Shelly/shelly-design-system';
	import ControlOnOff from 'src/components/Controls/Single/ControlOnOff.svelte';
	import ControlOnOffAddon from 'src/components/Controls/Single/ControlOnOffAddon.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import PeakPowerInfoV2 from 'src/components/Consumption/PeakPowerInfoV2.svelte';
	import EmeterDataG1 from 'src/components/EmeterData/EmeterDataG1.svelte';
	import EmeterDataG2V2 from 'src/components/EmeterData/EmeterDataG2V2.svelte';
	import EmeterDataG2PhasesV2 from 'src/components/EmeterData/EmeterDataG2PhasesV2.svelte';
	import CustomBoundary from 'src/components/ErrorBoundary/CustomBoundary.svelte';
	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import SidepanelPeekTray from 'src/components/Sidepanel/SidepanelPeekTray.svelte';
	import DeviceViewStateWrapper from '../DeviceViewStateWrapper.svelte';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceConfig = devices_config?.[device?.type];
	$: gen2 = is_gen_2(device);
	$: isSingleChannel = isGen2EM(device?.type) || device?.mode === 'monophase';
	$: metric = $emeterMainMetric as EmeterMainMetric;
	$: ctNotCalibrated = !!status?.[get_gen_2_component_channel_structure(device)]?.errors?.includes(
		'ct_type_not_set',
	);
	$: currentAvailable =
		!ctNotCalibrated && hasEmeterCurrentReading(status, device, isSingleChannel);
	$: effectiveMetric =
		metric === 'current' && !currentAvailable ? ('power' as EmeterMainMetric) : metric;
	$: heroReading = getEmeterHeroReading(status, device, effectiveMetric, isSingleChannel);

	let timerLabels: Record<string, any> = {};
	let emeter_timer: boolean | undefined;
	let peekExpanded = false;

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];
	$: peripheral = device && status ? getAddonStatus(device, [], false, true) : [];
	$: emHasSwitch = !!(status && 'switch:0' in status);
	$: emHasSwitchAddon = peripheral?.find((i) => i.peripheralType == 'switch');
	$: showPeekTray = gen2 && (emHasSwitch || !!emHasSwitchAddon);
	$: switchIsOn = (() => {
		if (emHasSwitch && status?.['switch:0']) {
			return !!status['switch:0']?.output;
		}
		if (emHasSwitchAddon?.sensor?.component) {
			return !!status?.[emHasSwitchAddon.sensor.component]?.output;
		}
		return false;
	})();

	onMount(() => {
		sidepanelChartMode.set(true);
	});

	onDestroy(() => {
		sidepanelChartMode.set(false);
	});

	beforeUpdate(() => {
		if (status?.online && deviceConfig) {
			emeter_timer = !gen2 && device?.category === 'emeter' && status?.['relays']?.[0].has_timer;
		}
	});

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	$: totalEnergyValue = (() => {
		if (!status || device == null) return undefined;
		if (gen2) {
			const emData =
				status?.[`${get_gen_2_component_from_category(device)}data:${device?.channel}`];
			return emData?.total_act ?? emData?.total_act_energy;
		}
		return status?.emeters?.[device?.channel]?.total;
	})();
	$: totalEnergyDisplay =
		totalEnergyValue != null && !Number.isNaN(totalEnergyValue)
			? { value: convert(totalEnergyValue, 'Wh'), hasValue: true }
			: { value: 'N/A', hasValue: false };

	const onSelectMetric = (next: EmeterMainMetric) => {
		if (next === 'current' && !currentAvailable) return;
		setEmeterMainMetric(next);
	};
</script>

<DeviceViewStateWrapper
	{device}
	hideSlotStaticBottom={!showPeekTray && gen2}
	staticBottomTopSpacing={showPeekTray && peekExpanded ? '0' : 'm'}
>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if emeter_timer}
			{#key status['relays'][0].timer_started}
				<TimerRemaining timerKey="gen1_emeter" {device} {status} {settings} bind:timerLabels />
			{/key}
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div class="flex-full-height justify-content-top" slot="content">
		{#if gen2}
			<ButtonGroupV2
				aria-label={$format('label_emeter_metric_group', {
					default: 'Show power or current',
				})}
				noWrap
				class="width-full mb-m"
				styleType="body-extra-small"
			>
				<Button
					class={effectiveMetric === 'power' ? 'active' : ''}
					size="m"
					aria-label={$format('button_emeter_metric_power', { default: 'Power' })}
					on:click={() => onSelectMetric('power')}
				>
					{$format('button_emeter_metric_power', { default: 'Power' })}
				</Button>
				<Button
					class={effectiveMetric === 'current' ? 'active' : ''}
					size="m"
					disabled={!currentAvailable}
					aria-label={$format('button_emeter_metric_current', { default: 'Current' })}
					on:click={() => onSelectMetric('current')}
				>
					{$format('button_emeter_metric_current', { default: 'Current' })}
				</Button>
			</ButtonGroupV2>

			<div class="mb-m">
				<HeroValue
					layout="stacked"
					size="l"
					label={effectiveMetric === 'power'
						? $format('em_power', { default: 'power' })
						: $format('em_current', { default: 'current' })}
					value={heroReading.display}
				/>
			</div>

			<CustomBoundary
				onError={console.error}
				message={'Problem loading emeter data. Please contact App support.'}
			>
				{#if isSingleChannel}
					<EmeterDataG2PhasesV2 {device} {status} metric={effectiveMetric} />
				{:else}
					<EmeterDataG2V2 {device} {status} metric={effectiveMetric} />
				{/if}
			</CustomBoundary>
		{:else if status?.emeters}
			<EmeterDataG1 {device} {status} />
		{/if}

		{#if totalEnergyDisplay.hasValue || deviceChartsConfig.length}
			<button
				type="button"
				class="emeter-total-energy mb-s"
				class:emeter-total-energy--clickable={!!deviceChartsConfig.length}
				data-prevent-drawer-swipe
				data-prevent-sidepanel-swipe
				disabled={!deviceChartsConfig.length}
				aria-label={$format('text_total_energy', { default: 'Total Energy' })}
				on:click={() => deviceChartsConfig.length && navigateToHistoryStatisticsTab()}
			>
				<Text styleType="miniature" class="text-color-muted">
					{$format('text_total_energy', { default: 'Total Energy' })}
				</Text>
				<Text styleType="body-small" class="text-600">{totalEnergyDisplay.value}</Text>
			</button>
		{/if}

		<PeakPowerInfoV2 {device} />
	</div>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		{#if !gen2}
			<ControlOnOff {device} {status} {settings} isWide useV2Components={true} />
		{:else if showPeekTray}
			<SidepanelPeekTray bind:expanded={peekExpanded}>
				<svelte:fragment slot="peek">
					<Text styleType="miniature" class="text-color-muted">
						{switchIsOn
							? $format('label_peek_tray_on', { default: 'On' })
							: $format('label_peek_tray_off', { default: 'Off' })}
					</Text>
				</svelte:fragment>

				{#if emHasSwitchAddon && emHasSwitch}
					<div class="d-flex gap-s">
						<div class="control-flex-column flex-1">
							<ControlOnOffAddon
								{device}
								{status}
								isInSlot
								bare
								useV2Components={true}
								component={emHasSwitchAddon?.sensor?.component}
							/>
						</div>
						<div class="control-flex-column flex-1">
							<ControlOnOff
								{device}
								{status}
								{settings}
								isInSlot
								bare
								useV2Components={true}
							/>
						</div>
					</div>
				{:else if emHasSwitch && !emHasSwitchAddon}
					<ControlOnOff {device} {status} {settings} isWide bare useV2Components={true} />
				{:else if emHasSwitchAddon}
					<ControlOnOffAddon
						{device}
						{status}
						isWide
						bare
						useV2Components={true}
						component={emHasSwitchAddon?.sensor?.component}
					/>
				{/if}
			</SidepanelPeekTray>
		{/if}
	</div>
</DeviceViewStateWrapper>

<style>
	.emeter-total-energy {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-s);
		width: 100%;
		padding: var(--spacing-xs) 0;
		border: 0;
		background: transparent;
		color: inherit;
		text-align: left;
	}

	.emeter-total-energy--clickable {
		cursor: pointer;
	}

	.emeter-total-energy:disabled {
		cursor: default;
	}
</style>
